<template>
  <v-container class="cuenta-page">
    <div class="mb-2">
      <v-btn
        text
        color="primary"
        class="px-2 back-btn"
        :ripple="false"
        @click="volverAlInicio"
      >
        <v-icon left>mdi-arrow-left</v-icon>
        {{ volverCuentaLabel }}
      </v-btn>
    </div>

    <div class="page-top">
      <div>
        <h1 class="page-title">Mi cuenta</h1>
        <p class="page-subtitle">
          {{ isLoggedIn
            ? `Sesión con ${providerLabel}`
            : 'Iniciá sesión para guardar tus datos y reutilizarlos' }}
        </p>
      </div>
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="primary"
        size="22"
        width="2"
      />
    </div>

    <v-alert v-if="authRedirectError" type="error" dense outlined class="mb-3 alert-soft">
      <div class="font-weight-medium">No se pudo completar el inicio de sesión</div>
      <div class="text-caption mt-2" style="white-space: pre-wrap;">{{ authRedirectError }}</div>
      <div class="text-caption mt-2 grey--text">
        Revisá en Firebase Console → Authentication → Settings → Authorized domains que esté <strong>localhost</strong>
        (y el dominio real en producción).
      </div>
    </v-alert>

    <template v-if="isLoggedIn">
      <div class="panel session-panel">
        <div class="session-row">
          <v-avatar color="primary" size="44" class="mr-3">
            <v-icon dark>mdi-account</v-icon>
          </v-avatar>
          <div class="session-meta">
            <div class="session-email">{{ userEmail || 'Usuario' }}</div>
            <div class="session-provider">Conectado con {{ providerLabel }}</div>
          </div>
        </div>
        <v-btn
          color="primary"
          rounded
          block
          class="mt-3 action-primary"
          @click="goMisPedidos"
        >
          <v-icon left>mdi-history</v-icon>
          {{ isCuentaGlobalRoute && !tiendaClientCode ? 'Mis pedidos (todas las tiendas)' : 'Mis pedidos' }}
        </v-btn>
      </div>

      <div class="panel">
        <h2 class="panel-title">Datos personales</h2>

        <v-alert v-if="saveStatus === 'ok'" type="success" dense outlined class="mb-3 alert-soft">
          Datos guardados.
        </v-alert>
        <v-alert v-else-if="saveStatus === 'error'" type="error" dense outlined class="mb-3 alert-soft">
          No se pudieron guardar los datos. Intentá de nuevo.
        </v-alert>

        <v-form ref="form" lazy-validation>
          <v-text-field
            v-model.trim="form.apellidoNombre"
            label="Nombre y apellido"
            prepend-inner-icon="mdi-account"
            outlined
            dense
            hide-details="auto"
            class="mb-3"
            :disabled="saving"
            :rules="[v => !!v || 'Completá tu nombre']"
          />

          <v-text-field
            v-model.trim="form.telefono"
            label="Teléfono"
            prepend-inner-icon="mdi-cellphone"
            outlined
            dense
            hide-details="auto"
            class="mb-3"
            :disabled="saving"
            :rules="[v => !v || String(v).length >= 6 || 'Teléfono demasiado corto']"
          />

          <v-btn
            color="primary"
            rounded
            block
            class="action-primary"
            :loading="saving"
            :disabled="saving"
            @click="onGuardar"
          >
            Guardar datos personales
          </v-btn>
        </v-form>
      </div>

      <div class="panel">
        <div class="panel-header">
          <h2 class="panel-title mb-0">Mis direcciones</h2>
          <v-btn text color="primary" class="action-text" @click="openNewAddress">
            <v-icon left small>mdi-plus</v-icon>
            Nueva
          </v-btn>
        </div>

        <v-alert v-if="addressSaveStatus === 'ok'" type="success" dense outlined class="mb-3 alert-soft">
          Dirección guardada.
        </v-alert>
        <v-alert v-else-if="addressSaveStatus === 'error'" type="error" dense outlined class="mb-3 alert-soft">
          No se pudo guardar la dirección.
        </v-alert>

        <div v-if="addresses.length" class="address-list">
          <div
            v-for="a in addresses"
            :key="a.CliClienteDir_id"
            class="address-card"
          >
            <div class="address-main">
              <div class="address-alias">
                {{ a.Alias || 'Sin alias' }}
                <v-chip
                  v-if="a.EsPrincipal"
                  x-small
                  label
                  class="ml-2"
                  color="primary"
                  text-color="white"
                >
                  Principal
                </v-chip>
              </div>
              <div class="address-text">{{ a.DireccionTexto }}</div>
            </div>
            <div class="address-actions">
              <v-btn icon small @click="openEditAddress(a)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                :disabled="addresses.length <= 1"
                @click="onDeleteAddress(a)"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
        <v-alert v-else type="info" dense outlined class="mb-0 alert-soft">
          Aún no cargaste direcciones. Agregá al menos una para usarla en el checkout.
        </v-alert>
      </div>

      <v-btn
        text
        color="primary"
        block
        class="logout-btn"
        :loading="loading"
        @click="onLogout"
      >
        Cerrar sesión
      </v-btn>
    </template>

    <template v-else>
      <div class="panel">
        <p class="login-copy">
          Iniciá sesión para guardar tus datos y reutilizarlos en próximos pedidos, incluso en otro dispositivo.
        </p>
        <v-btn
          color="primary"
          rounded
          block
          large
          class="action-primary"
          :loading="loading"
          @click="onLoginGoogle"
        >
          <v-icon left>mdi-google</v-icon>
          Continuar con Google
        </v-btn>
      </div>
    </template>

    <v-dialog v-model="addressDialog" max-width="520" persistent content-class="cuenta-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          {{ addressEditor.CliClienteDir_id ? 'Editar dirección' : 'Nueva dirección' }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-text-field
            v-model.trim="addressEditor.Alias"
            label="Alias (casa, trabajo…)"
            prepend-inner-icon="mdi-tag-outline"
            outlined
            dense
            hide-details="auto"
            class="mb-3"
          />
          <v-text-field
            v-model.trim="addressEditor.DireccionTexto"
            label="Dirección"
            prepend-inner-icon="mdi-map-marker"
            outlined
            dense
            hide-details="auto"
            class="mb-2"
            readonly
            :placeholder="addressEditor.Lat != null && addressEditor.Lng != null ? ' ' : 'Elegí la dirección en el mapa para geolocalizarla.'"
            :rules="[
              v => !!v || 'Elegí la dirección en el mapa',
              () => (addressEditor.Lat != null && addressEditor.Lng != null) || 'Falta geolocalizar la dirección (Lat/Lng)'
            ]"
            @click="mapDialog = true"
          />
          <v-checkbox
            v-model="addressEditor.EsPrincipal"
            label="Usar como dirección principal"
            dense
            hide-details
            class="mt-0"
          />
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-btn text class="action-text" @click="closeAddressDialog">
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            rounded
            class="action-primary"
            :loading="addressSaving"
            @click="onSaveAddress"
          >
            Guardar dirección
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="mapDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <GoogleMapSelector
        v-if="mapDialog"
        omitir-validacion-zona
        :key="'cuenta-map-' + String(addressEditor.CliClienteDir_id || 'new')"
        :initial-address-text="addressEditor.DireccionTexto"
        :initial-lat="addressEditor.Lat"
        :initial-lng="addressEditor.Lng"
        @cerrar-map-selector="mapDialog = false"
      />
    </v-dialog>

    <v-dialog
      v-model="deleteConfirmDialog"
      max-width="420"
      content-class="cuenta-dialog"
      @click:outside="cancelDeleteAddress"
    >
      <v-card class="dialog-card">
        <v-card-title class="d-flex align-center pb-2 dialog-title">
          <v-avatar color="error lighten-5" size="40" class="mr-3">
            <v-icon color="error">mdi-delete-alert-outline</v-icon>
          </v-avatar>
          <span>Eliminar dirección</span>
        </v-card-title>
        <v-card-text class="pt-0">
          <p class="text-body-2 mb-2">
            ¿Seguro que querés eliminar esta dirección? No podés deshacer esta acción.
          </p>
          <div v-if="addressPendingDelete" class="delete-preview">
            <div class="address-alias">
              {{ addressPendingDelete.Alias || 'Sin alias' }}
            </div>
            <div class="address-text">
              {{ addressPendingDelete.DireccionTexto }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="px-4 pb-4 pt-0">
          <v-spacer />
          <v-btn text class="action-text" :disabled="addressDeleting" @click="cancelDeleteAddress">
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            rounded
            class="action-primary"
            :loading="addressDeleting"
            @click="confirmDeleteAddress"
          >
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import GoogleMapSelector from '@/components/checkout/GoogleMapSelector.vue'
import { bus } from '@/helpers/bus.js'
import { isFirebaseDebug } from '@/helpers/firebaseDebug'
import { getGlobalAccountClientCode } from '@/helpers/globalAccountClientCode'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  name: 'CuentaView',
  components: { GoogleMapSelector },
  computed: {
    ...mapState('Cuenta', ['loading', 'authRedirectError', 'authRedirectDebug', 'me']),
    ...mapGetters('Cuenta', ['isLoggedIn', 'userEmail']),
    firebaseDebugOn () {
      return isFirebaseDebug()
    },
    providerLabel () {
      const p = this.me && this.me.FirebaseProvider ? String(this.me.FirebaseProvider).toLowerCase() : ''
      if (p === 'google') return 'Google'
      if (p) return p
      return 'Google'
    },
    /** Ruta dedicada: perfil global, sin `client` en la URL. */
    isCuentaGlobalRoute () {
      return this.$route.name === 'CuentaGlobal'
    },
    /** Código de tienda solo para llamadas API (portal); en `/tiendas/cuenta` no aparece en la URL. */
    clientCodeForApi () {
      if (this.isCuentaGlobalRoute) return getGlobalAccountClientCode()
      const c = this.$route && this.$route.params ? this.$route.params.client : null
      return c || null
    },
    /** Tienda real para historial de pedidos (no el portal global). */
    tiendaClientCode () {
      const q = this.$route.query || {}
      if (q.client) return q.client
      if (this.$route.params && this.$route.params.client) return this.$route.params.client
      return null
    },
    addresses () {
      const m = this.me
      if (!m || !m.Addresses) return []
      return Array.isArray(m.Addresses) ? m.Addresses : []
    },
    /** Texto del botón atrás en `/tiendas/cuenta` según origen y entorno. */
    volverCuentaLabel () {
      if (!this.isCuentaGlobalRoute) return 'Volver al inicio'
      const q = this.$route.query || {}
      if (q.from === 'store' && q.client) return 'Volver a la tienda'
      if (q.from === 'sucursales' && q.franquicia) return 'Volver'
      if (this.$isMobile()) return 'Volver a tiendas'
      return 'Volver'
    }
  },
  watch: {
    isLoggedIn: {
      immediate: true,
      async handler (val) {
        if (!val) return
        await this.hydrateMe()
      }
    },
    deleteConfirmDialog (val) {
      if (!val && !this.addressDeleting) {
        this.addressPendingDelete = null
      }
    }
  },
  mounted () {
    this._onMapAddress = (text) => {
      if (typeof text === 'string' && text) this.addressEditor.DireccionTexto = text
    }
    this._onMapUrl = (url) => {
      if (typeof url === 'string' && url) this.addressEditor.GoogleMapsUrl = url
    }
    this._onMapLatLng = (payload) => {
      if (!payload || payload.lat == null || payload.lng == null) return
      this.addressEditor.Lat = Number(payload.lat)
      this.addressEditor.Lng = Number(payload.lng)
    }
    bus.$on('onDomicilioGoogleMapSelectorChange', this._onMapAddress)
    bus.$on('onGoogleMapsUrlChange', this._onMapUrl)
    bus.$on('onGoogleMapsLatLngChange', this._onMapLatLng)
  },
  beforeDestroy () {
    bus.$off('onDomicilioGoogleMapSelectorChange', this._onMapAddress)
    bus.$off('onGoogleMapsUrlChange', this._onMapUrl)
    bus.$off('onGoogleMapsLatLngChange', this._onMapLatLng)
  },
  methods: {
    ...mapActions('Cuenta', [
      'loginGoogle',
      'logout',
      'fetchMe',
      'saveMe',
      'saveCustomerAddress',
      'updateCustomerAddress',
      'deleteCustomerAddress'
    ]),
    async onLoginGoogle () {
      await this.loginGoogle()
    },
    async onLogout () {
      await this.logout()
    },
    goMisPedidos () {
      const client = this.tiendaClientCode
      if (client) {
        this.$router.push({ name: 'MisPedidos', params: { client } }).catch(() => {})
        return
      }
      this.$router.push({ name: 'MisPedidosGlobal' }).catch(() => {})
    },
    volverAlInicio () {
      if (this.isCuentaGlobalRoute) {
        const q = this.$route.query || {}
        const returnClient = typeof q.client === 'string' ? q.client.trim() : ''
        const fromStore = q.from === 'store' && returnClient

        if (fromStore) {
          const mesaId = q.mesaId
          if (mesaId) {
            this.$router.push({
              name: 'MesaQR',
              params: { client: returnClient, mesaId: String(mesaId) }
            }).catch(() => {})
            return
          }
          const homeName = q.home === 'HomeShort' ? 'HomeShort' : 'Home'
          this.$router.push({ name: homeName, params: { client: returnClient } }).catch(() => {})
          return
        }

        if (q.from === 'sucursales' && q.franquicia) {
          this.$router.push({ name: 'Sucursales', params: { id: String(q.franquicia) } }).catch(() => {})
          return
        }

        if (this.$isMobile()) {
          this.$router.push({ name: 'Tiendas' }).catch(() => {})
        } else {
          window.location.href = 'https://info.clickaway.app/'
        }
        return
      }
      const client = this.$route.params.client
      if (!client) {
        this.$router.push({ name: 'Tiendas' }).catch(() => {})
        return
      }
      if (this.$route.params.mesaId) {
        this.$router.push({
          name: 'MesaQR',
          params: { client, mesaId: this.$route.params.mesaId }
        }).catch(() => {})
        return
      }
      if (this.$route.name === 'HomeShort') {
        this.$router.push({ name: 'HomeShort', params: { client } }).catch(() => {})
      } else {
        this.$router.push({ name: 'Home', params: { client } }).catch(() => {})
      }
    },
    resetAddressEditor () {
      this.addressEditor = {
        CliClienteDir_id: null,
        Alias: '',
        DireccionTexto: '',
        Lat: null,
        Lng: null,
        GoogleMapsUrl: '',
        EsPrincipal: false
      }
    },
    openNewAddress () {
      this.resetAddressEditor()
      this.addressEditor.EsPrincipal = this.addresses.length === 0
      this.addressSaveStatus = null
      this.addressDialog = true
    },
    openEditAddress (row) {
      this.addressEditor = {
        CliClienteDir_id: row.CliClienteDir_id,
        Alias: row.Alias || '',
        DireccionTexto: row.DireccionTexto || '',
        Lat: row.Lat != null ? Number(row.Lat) : null,
        Lng: row.Lng != null ? Number(row.Lng) : null,
        GoogleMapsUrl: row.GoogleMapsUrl || '',
        EsPrincipal: !!row.EsPrincipal
      }
      this.addressSaveStatus = null
      this.addressDialog = true
    },
    closeAddressDialog () {
      this.addressDialog = false
      this.resetAddressEditor()
    },
    async hydrateMe () {
      const clientCode = this.clientCodeForApi
      if (!clientCode) return
      await this.fetchMe({ clientCode })
      const me = this.me || {}
      this.form.apellidoNombre = me.ApellidoNombre || this.form.apellidoNombre || ''
      this.form.telefono = me.Telefono || this.form.telefono || ''
    },
    async onGuardar () {
      const ok = this.$refs.form ? this.$refs.form.validate() : true
      if (!ok) return
      this.saving = true
      this.saveStatus = null
      try {
        const clientCode = this.clientCodeForApi
        const res = await this.saveMe({
          clientCode,
          payload: {
            ApellidoNombre: this.form.apellidoNombre,
            Telefono: this.form.telefono
          }
        })
        this.saveStatus = res ? 'ok' : 'error'
      } finally {
        this.saving = false
      }
    },
    async onSaveAddress () {
      const clientCode = this.clientCodeForApi
      if (!clientCode || !this.addressEditor.DireccionTexto) {
        this.addressSaveStatus = 'error'
        return
      }
      if (this.addressEditor.Lat == null || this.addressEditor.Lng == null) {
        this.addressSaveStatus = 'error'
        return
      }
      this.addressSaving = true
      this.addressSaveStatus = null
      try {
        const payload = {
          Alias: this.addressEditor.Alias || null,
          DireccionTexto: this.addressEditor.DireccionTexto,
          Lat: this.addressEditor.Lat,
          Lng: this.addressEditor.Lng,
          GoogleMapsUrl: this.addressEditor.GoogleMapsUrl || null,
          EsPrincipal: !!this.addressEditor.EsPrincipal
        }
        let ok = false
        if (this.addressEditor.CliClienteDir_id) {
          const data = await this.updateCustomerAddress({
            clientCode,
            id: this.addressEditor.CliClienteDir_id,
            payload
          })
          ok = !!data
        } else {
          const data = await this.saveCustomerAddress({ clientCode, payload })
          ok = !!data
        }
        this.addressSaveStatus = ok ? 'ok' : 'error'
        if (ok) {
          this.addressDialog = false
          this.resetAddressEditor()
        }
      } finally {
        this.addressSaving = false
      }
    },
    onDeleteAddress (row) {
      if (this.addresses.length <= 1) return
      this.addressPendingDelete = row
      this.deleteConfirmDialog = true
    },
    cancelDeleteAddress () {
      if (this.addressDeleting) return
      this.deleteConfirmDialog = false
    },
    async confirmDeleteAddress () {
      const row = this.addressPendingDelete
      if (!row) return
      const clientCode = this.clientCodeForApi
      if (!clientCode) {
        this.cancelDeleteAddress()
        return
      }
      this.addressDeleting = true
      this.addressSaveStatus = null
      try {
        const data = await this.deleteCustomerAddress({ clientCode, id: row.CliClienteDir_id })
        this.addressSaveStatus = data ? 'ok' : 'error'
        if (data) {
          this.deleteConfirmDialog = false
          this.addressPendingDelete = null
        }
      } finally {
        this.addressDeleting = false
      }
    }
  },
  data () {
    return {
      saving: false,
      saveStatus: null,
      addressSaving: false,
      addressDeleting: false,
      addressSaveStatus: null,
      addressDialog: false,
      mapDialog: false,
      deleteConfirmDialog: false,
      addressPendingDelete: null,
      form: {
        apellidoNombre: '',
        telefono: ''
      },
      addressEditor: {
        CliClienteDir_id: null,
        Alias: '',
        DireccionTexto: '',
        Lat: null,
        Lng: null,
        GoogleMapsUrl: '',
        EsPrincipal: false
      }
    }
  }
}
</script>

<style scoped>
.cuenta-page {
  max-width: 640px;
  text-align: left;
  padding-bottom: 32px;
}
.back-btn,
.action-text,
.logout-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
}
.page-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.page-title {
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
}
.page-subtitle {
  margin: 4px 0 0;
  color: rgba(0, 0, 0, 0.5);
  font-size: 0.9rem;
}
.panel {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 12px;
}
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}
.panel-title {
  margin: 0 0 12px;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.87);
}
.session-row {
  display: flex;
  align-items: center;
}
.session-email {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.3;
  word-break: break-word;
}
.session-provider {
  margin-top: 2px;
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.5);
}
.action-primary {
  text-transform: none !important;
  letter-spacing: 0.02em !important;
  font-weight: 700 !important;
  border-radius: 12px !important;
  height: 44px !important;
}
.login-copy {
  margin: 0 0 16px;
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.45;
}
.address-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.address-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
  background: #fafafa;
  border: 1px solid #efefef;
  border-radius: 12px;
}
.address-main {
  min-width: 0;
  flex: 1;
}
.address-alias {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 0.95rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
}
.address-text {
  margin-top: 4px;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.35;
}
.address-actions {
  display: flex;
  flex-shrink: 0;
}
.alert-soft {
  border-radius: 10px !important;
}
.logout-btn {
  margin-top: 4px;
}
.dialog-card {
  border-radius: 16px !important;
}
.dialog-title {
  font-size: 1.15rem !important;
  font-weight: 800 !important;
}
.delete-preview {
  padding: 12px;
  background: #f5f5f5;
  border-radius: 12px;
}
</style>

<style>
.cuenta-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}
</style>
